import styles from './AuthMenu.module.css'
import clsx from 'clsx'
import { BasicStyledComponent } from 'shared-definitions/types'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { AuthContext } from '../contexts/AuthContext'
import { ThemeContext } from '../contexts/ThemeContext'
import Toggle from './Toggle'
import { CommonDataContext } from '../contexts/CommonDataContext'
import { CommentHubNotificationGroup } from 'shared-code/commenthub-api'
import { UseNotificationsResult } from '../hooks/use-notifications'
import ErrorBoundary from '../service/ErrorBoundary'
import Linked from '../service/Linked'
import { AccountAvatar } from '../account/AccountAvatar'
import { useDarkAccent } from '../account/accent'
import { accountHref } from '../account/routes'
import { BellIcon, GearIcon, LogOutIcon, MoonIcon } from './MenuIcons'

const NotificationsTray = dynamic(() => import('../comments/NotificationsTray'), { ssr: false })

/** A row of the menu: an app's page (SoundGuys' "My gear", "Help & support"). */
export interface AuthMenuItem {
  id: string
  label: string
  href: string
  icon: FC<React.SVGAttributes<SVGElement>>
  /** A quiet figure at the row's end ("2"); absent = none. */
  meta?: string
  /** A pill after the label (SoundGuys' "NEW" on Stats); absent = none. */
  badge?: ReactNode
}

/** In `primaryItems`, where the shared Notifications row (it opens the tray) goes. */
export const NOTIFICATIONS_ENTRY = 'notifications' as const

export type AuthMenuEntry = AuthMenuItem | typeof NOTIFICATIONS_ENTRY

/**
 * An app's own notifications page (both apps: `/profile/notifications`, or SoundGuys' member hub
 * tab): the tray's "Settings" links to `settings` instead of opening its inline view, and
 * "See all" links to `all`. Absent = the inline settings and no See all.
 */
export interface NotificationLinks {
  settings: string
  all: string
  /** A row's own link (the tray's `itemHref`); undefined = the row's. */
  itemHref?: (group: CommentHubNotificationGroup) => string | undefined
}

/**
 * The menu's identity header — ONE link to the reader's page. Every field is optional; the
 * defaults are the session's: the reader's photo or initials, their name, their email as the
 * second line, and "My profile ›" to the app's `profileLink`. An app overrides what it knows
 * better (SoundGuys: its letter disc, "tier · points · @handle", "My page ›" to the member hub).
 * Copy stays in the app (6496d8bc1): the package only holds the defaults both brands share.
 */
export interface AuthMenuIdentity {
  /** The second line; absent = the reader's email. */
  line?: ReactNode
  /** Where the header goes; absent = the app's `profileLink`. */
  href?: string
  /** The right-hand call to action, without the chevron; absent = "My profile". */
  cta?: string
  /** The 44px disc; absent = `AccountAvatar` (photo or initials). */
  avatar?: ReactNode
  /** The line is on its way (a known member whose page facts are loading): hold its place empty
   *  rather than show the email and then swap it. */
  pending?: boolean
}

interface AuthMenuProps extends BasicStyledComponent {
  onActionComplete?: () => void
  /** Rows between Notifications and Settings when the app passes no `primaryItems`. */
  items?: ReadonlyArray<AuthMenuItem>
  /** Absent for a reader with no commentHub session — the row is then not shown. */
  notifications?: UseNotificationsResult
  /**
   * The menu stays mounted and is shown with a class, so it needs to be told
   * when it closes: the notifications view must not survive to the next open,
   * and the tray must unmount so that reopening it counts as a fresh open and
   * moves the seen watermark again.
   */
  menuOpen?: boolean
  notificationLinks?: NotificationLinks
  /** The identity header's overrides — see `AuthMenuIdentity`. */
  identity?: AuthMenuIdentity
  /**
   * The main rows, with `NOTIFICATIONS_ENTRY` marking where the tray row goes. Absent =
   * Notifications, then `items`, then Settings (`/profile/settings`). One layout either way:
   * brands differ by the rows they pass and their palette, never by a second markup (1541246dc).
   */
  primaryItems?: ReadonlyArray<AuthMenuEntry>
  /** Rows that lead the closing group (before Dark mode and Log out). */
  quietItems?: ReadonlyArray<AuthMenuItem>
}

const AuthMenu: FC<AuthMenuProps> = ({
  className,
  onActionComplete,
  items = [],
  notifications,
  menuOpen = true,
  notificationLinks,
  identity = {},
  primaryItems,
  quietItems = [],
}) => {
  const {
    common: { profileLink },
  } = CommonDataContext.useContainer()
  const { theme, setTheme } = ThemeContext.useContainer()
  const { logout, authState } = AuthContext.useContainer()
  const darkAccent = useDarkAccent()
  const [showNotifications, setShowNotifications] = useState(false)
  // The tray's back button swaps the tray out for the rows: the Notifications row it came from
  // takes focus again, or a keyboard reader lands on <body>. Only after a back, never on close.
  const notificationsRowRef = useRef<HTMLButtonElement>(null)
  const backToRowRef = useRef(false)
  useEffect(() => {
    if (!showNotifications && backToRowRef.current) {
      backToRowRef.current = false
      notificationsRowRef.current?.focus()
    }
  }, [showNotifications])

  useEffect(() => {
    if (!menuOpen) {
      setShowNotifications(false)
    }
  }, [menuOpen])

  const close = (): void => onActionComplete?.()
  const rootClass = clsx(className, styles.container, darkAccent && styles.darkAccent)

  if (notifications && showNotifications) {
    return (
      <div className={clsx(rootClass, styles.containerTray)}>
        {/* This menu is site chrome — it renders on every route — so a runtime
            error inside the tray must cost the reader their notifications, not
            the page. Same reason Comments.tsx wraps the conversation. */}
        <ErrorBoundary
          fallback={<div className={styles.trayError}>{"Notifications aren't available"}</div>}
        >
          <NotificationsTray
            inline
            notifications={notifications}
            onBack={() => {
              backToRowRef.current = true
              setShowNotifications(false)
            }}
            onClose={close}
            settingsHref={notificationLinks?.settings}
            seeAllHref={notificationLinks?.all}
            itemHref={notificationLinks?.itemHref}
          />
        </ErrorBoundary>
      </div>
    )
  }

  const user = authState?.user
  const name = user?.name?.trim() || user?.email || ''
  const line = identity.pending ? null : identity.line ?? user?.email ?? null
  const unread = notifications?.unread ?? 0
  const unreadLabel = notifications?.capped ? '99+' : String(unread)

  const settingsItem: AuthMenuItem = {
    id: 'settings',
    label: 'Settings',
    href: accountHref(profileLink.href, 'settings'),
    icon: GearIcon,
  }
  const rows: ReadonlyArray<AuthMenuEntry> = primaryItems ?? [
    NOTIFICATIONS_ENTRY,
    ...items,
    settingsItem,
  ]

  const itemRow = (item: AuthMenuItem): JSX.Element => (
    <Linked key={item.id} href={item.href} className={styles.row} onClick={close}>
      <item.icon className={styles.icon} />
      <span className={styles.label}>{item.label}</span>
      {item.badge}
      {item.meta ? <span className={styles.meta}>{item.meta}</span> : null}
    </Linked>
  )

  const notificationsRow = notifications ? (
    <button
      key={NOTIFICATIONS_ENTRY}
      ref={notificationsRowRef}
      type="button"
      className={styles.row}
      onClick={() => setShowNotifications(true)}
    >
      <BellIcon className={styles.icon} />
      <span className={styles.label}>{'Notifications'}</span>
      {unread > 0 ? (
        <span className={styles.count} aria-label={`${unreadLabel} unread`}>
          {unreadLabel}
        </span>
      ) : null}
    </button>
  ) : null

  return (
    <div className={rootClass}>
      <Linked href={identity.href ?? profileLink.href} className={styles.head} onClick={close}>
        <span className={styles.headAvatar}>
          {identity.avatar ?? <AccountAvatar size={44} tone="accent" name={name} />}
        </span>
        <span className={styles.headText}>
          <span className={styles.headName}>{name || 'Your account'}</span>
          {line || identity.pending ? (
            <span className={styles.headLine} aria-busy={identity.pending || undefined}>
              {line}
            </span>
          ) : null}
        </span>
        <span className={styles.headCta}>{`${identity.cta ?? 'My profile'} ›`}</span>
      </Linked>

      <span className={styles.divider} aria-hidden="true" />

      {rows.map(entry => (entry === NOTIFICATIONS_ENTRY ? notificationsRow : itemRow(entry)))}

      <span className={styles.divider} aria-hidden="true" />

      {quietItems.map(itemRow)}
      <button
        type="button"
        role="switch"
        aria-checked={theme === 'dark'}
        className={styles.row}
        onClick={() => {
          setTheme(theme === 'light' ? 'dark' : 'light')
          close()
        }}
      >
        <MoonIcon className={styles.icon} />
        <span className={styles.label}>{'Dark mode'}</span>
        <Toggle className={styles.toggle} isOn={theme === 'dark'} variant="grey" size="small" />
      </button>
      <button
        type="button"
        className={clsx(styles.row, styles.rowQuiet)}
        onClick={() => {
          void logout()
          close()
        }}
      >
        <LogOutIcon className={styles.icon} />
        <span className={styles.label}>{'Log out'}</span>
      </button>
    </div>
  )
}

export default AuthMenu
